'use client';

import { type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import getCtaStyles from '../button/get-cta-styles';
import RichTextTitle, { sanitizeRichTextTitle } from '../rich-text-title';
import ThematicCard from './thematic-card';
import type { ThematicCardData } from './types';

const MAX_CARDS = 3;

export type Props = {
	title: string;
	cards: ThematicCardData[];
	id?: string;
	sectionId?: string;
	ctaLabel?: string;
	ctaHref?: string;
	ctaNewWindow?: boolean;
	className?: string;
};

const columnRatios = (count: number) =>
	count === MAX_CARDS
		? { heading: 'md:flex-[293_1_0]', cards: 'md:flex-[937_1_0]' }
		: { heading: 'md:flex-[400_1_0]', cards: 'md:flex-[830_1_0]' };

const aspectFor = (count: number, index: number) => {
	if (count === 1) return 'aspect-[16/9]';
	if (count === 2) return 'aspect-[4/5] md:aspect-[4/3]';
	return index === 0 ? 'aspect-[16/9] md:aspect-[4/5]' : 'aspect-[4/5]';
};

const widthFor = (count: number, index: number) => {
	if (count === 1) return 'w-full';
	if (count === 2) return 'grow basis-[calc(50%-var(--spacing-150)/2)] md:flex-1 md:basis-0';
	return index === 0
		? 'w-full md:w-auto md:flex-1 md:basis-0'
		: 'grow basis-[calc(50%-var(--spacing-150)/2)] md:flex-1 md:basis-0';
};

export default function ThematicCards({
	title,
	cards,
	id,
	sectionId,
	ctaLabel,
	ctaHref,
	ctaNewWindow,
	className,
}: Props): ReactElement | null {
	const items = cards.slice(0, MAX_CARDS);
	const count = items.length;

	if (count === 0) return null;

	const ratios = columnRatios(count);
	const showCta = Boolean(ctaLabel && ctaHref);
	const hasHeading = Boolean(id) && Boolean(sanitizeRichTextTitle(title));

	return (
		<section
			id={sectionId}
			aria-labelledby={hasHeading ? id : undefined}
			// Left/right gutter via spacing-wide-grid, like every sibling section on the
			// page: the host page renders this component with no grid of its own, so
			// without it the content runs edge to edge (decision 9ed57b). Top/bottom
			// stay zero - the page supplies those (decision 78aee1).
			className={cn('w-full spacing-wide-grid', className)}
		>
			<div className="flex w-full flex-col gap-y-stack-l md:flex-row md:items-center md:gap-x-grid-gutter">
				<div className={cn('flex min-w-0 flex-col items-start gap-y-stack-l', ratios.heading)}>
					<RichTextTitle title={title} id={id} className="text-display3 text-text-heading" />

					{showCta && (
						<a
							href={ctaHref}
							target={ctaNewWindow ? '_blank' : undefined}
							rel={ctaNewWindow ? 'noopener' : undefined}
							className={getCtaStyles({ intent: 'filled', size: 'small' })}
						>
							{ctaLabel}
						</a>
					)}
				</div>

				<ul
					className={cn(
						'flex min-w-0 flex-wrap items-start gap-x-inline-m gap-y-stack-l md:flex-nowrap md:gap-inline-l',
						ratios.cards
					)}
				>
					{items.map((card, index) => (
						<ThematicCard
							key={card.id}
							card={card}
							aspectClassName={aspectFor(count, index)}
							widthClassName={widthFor(count, index)}
						/>
					))}
				</ul>
			</div>
		</section>
	);
}
